<template>
  <section class="inspector-steps">
    <Loading v-if="loading" />
    <b-steps size="is-small" v-model="activeStep">
      <template v-for="(step, index) in steps">
        <b-step-item
          v-if="step.displayed"
          :key="index"
          :visible="step.visible"
          :label="step.title"
        >
          <div v-if="index === 0" class="card">
            <div class="card-title">
              <h3>Vehicle ID: {{ vehicle.id }}</h3>
            </div>

            <div class="details-container">
              <div class="details">
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Year:</h4>
                    </div>
                    <strong>{{ vehicle.year }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Make:</h4>
                    </div>
                    <strong>{{ vehicle.make }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Model:</h4>
                    </div>
                    <strong>{{ vehicle.model }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Trim:</h4>
                    </div>
                    <strong>{{ vehicle.trim }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Odometer:</h4>
                    </div>
                    <strong>{{ vehicle.mileage }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Color:</h4>
                    </div>
                    <strong>{{ vehicle.color }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Location:</h4>
                    </div>
                    <strong>{{ vehicle.zip }}</strong>
                  </div>
                </li>
              </div>

              <div class="details">
                <li>
                  <div class="list-content">
                    <div>
                      <h4>VIN:</h4>
                    </div>
                    <strong>{{ vehicle.vin }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Transmission:</h4>
                    </div>
                    <strong>{{ vehicle.transmission }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Drivetrain:</h4>
                    </div>
                    <strong>{{ vehicle.drivetrain }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>engine:</h4>
                    </div>
                    <strong>{{ vehicle.engineDescription }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>fuel type:</h4>
                    </div>
                    <strong>{{ vehicle.fuelType }}</strong>
                  </div>
                </li>
                <li>
                  <div class="list-content">
                    <div>
                      <h4>Inspector:</h4>
                    </div>
                    <div class="inspector-details">
                      <strong>{{ inspector.profile.email }}</strong>
                      <strong
                        >{{ inspector.profile.firstName }}
                        {{ inspector.profile.lastName }}</strong
                      >
                    </div>
                  </div>
                </li>
              </div>
            </div>
          </div>
          <div v-if="index === 1" class="card">
            <Inspection
              v-bind:vehicle="vehicle"
              v-if="vehicle.bbFeatures"
              v-on:dataChanged="dataChanged"
            />
          </div>
          <div v-if="index === 2" class="photos-container">
            <div v-for="(gallery, index) in vehiclePhotos" :key="index">
              <h4>{{ index }} Photos</h4>
              <div class="btn-container">
                <b-field class="file">
                  <b-upload
                    class="file-label"
                    accept="image/*"
                    :multiple="true"
                    @change.native="uploadImage($event, index)"
                  >
                    <span class="file-cta">
                      <b-icon class="file-icon" icon="upload"></b-icon>
                    </span>
                  </b-upload>
                </b-field>
              </div>
              <div class="photo-added">
                <div
                  v-for="(url, i) in gallery.images"
                  class="photo-card"
                  :key="i"
                >
                  <div class="image-actions">
                    <button
                      class="file-cta"
                      :disabled="vehiclePhotos[index].images[i].progress === 0"
                      @click="remove(index, i)"
                    >
                      <b-icon class="file-icon" icon="trash-can"></b-icon>
                    </button>
                  </div>
                  <img
                    class="car-photo"
                    :src="vehiclePhotos[index].images[i].url"
                    :alt="index"
                  />
                  <div
                    class="progress"
                    v-if="vehiclePhotos[index].images[i].progress !== 0"
                  >
                    <b-progress
                      size="is-small"
                      type="is-success"
                      :value="vehiclePhotos[index].images[i].progress"
                    ></b-progress>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="index === 3">
            <div class="card">
              <div class="card-title">
                <h3>Questionnaire</h3>
              </div>
              <div class="details-container column">
                <div
                  class="questionnaire-details"
                  v-for="question in questionnaireData"
                  :key="question.title"
                >
                  <li>
                    <div class="list-content">
                      <div class="questionnaire-container">
                        <h2>{{ question.title }}</h2>
                        <div class="btn-selection">
                          <b-field>
                            <b-radio-button
                              type="is-primary"
                              value="Yes"
                              native-value="Yes"
                              :name="question.title"
                              v-model="question.response"
                            >
                              <span>Yes</span>
                            </b-radio-button>

                            <b-radio-button
                              type="is-primary"
                              value="No"
                              native-value="No"
                              :name="question.title"
                              v-model="question.response"
                            >
                              <span>No</span>
                            </b-radio-button>
                          </b-field>
                        </div>
                      </div>
                    </div>
                    <div
                      class="comment-section"
                      v-if="question.placeholder && question.response === 'Yes'"
                    >
                      <input
                        :placeholder="question.placeholder"
                        type="text"
                        v-model="question.commentary"
                      />
                    </div>
                  </li>
                </div>
              </div>
            </div>
            <div class="card">
              <div class="card-title">
                <h3>Auction Expectation</h3>
              </div>
              <div class="details-container column">
                <li>
                  <div class="list-content">
                    <div class="questionnaire-container">
                      <h2>
                        Based on our market data, we believe your vehicle will
                        bring between:
                      </h2>
                      <h3>
                        {{ (auctionExpectation * 0.92) | currency }} -
                        {{ (auctionExpectation * 1.08) | currency }}
                      </h3>
                    </div>
                  </div>
                </li>
                <li
                  v-for="expectation in expectationPriceData"
                  :key="expectation.title"
                >
                  <div class="list-content">
                    <div class="questionnaire-container">
                      <div>
                        <strong v-if="expectation.title === 'Reserve'">
                          The reserve price should be:
                        </strong>
                        <strong v-else>
                          We believe your "{{ expectation.title }}" price should
                          be
                          <em v-if="expectation.title === 'Buy-it-now'">{{
                            priceSugestions.buyItNow | currency
                          }}</em>
                          <em v-if="expectation.title === 'Starting-Price'">{{
                            priceSugestions.askingPrice | currency
                          }}</em
                          >. Do you agree?:
                        </strong>
                      </div>
                      <div
                        class="btn-selection"
                        v-if="expectation.title !== 'Reserve'"
                      >
                        <b-field>
                          <b-radio-button
                            type="is-primary"
                            value="Yes"
                            :name="expectation.title"
                            v-model="expectation.response"
                            native-value="Yes"
                          >
                            <span>Yes</span>
                          </b-radio-button>

                          <b-radio-button
                            type="is-primary"
                            value="No"
                            :name="expectation.title"
                            v-model="expectation.response"
                            native-value="No"
                          >
                            <span>No</span>
                          </b-radio-button>
                        </b-field>
                      </div>
                      <div class="comment-section">
                        <input
                          v-model="expectation.commentary"
                          placeholder="Enter your comment here"
                          type="text"
                        />
                      </div>
                    </div>
                  </div>
                </li>
              </div>
            </div>
          </div>
          <div v-if="index === 4">
            <AuctionDetails
              :inspectionData="inspectionData"
              :vehiclePhotos="vehiclePhotos"
            />
            <div class="submit-container">
              <button
                :disabled="!isFormFilled"
                @click="submitInspection"
                class="button is-primary"
              >
                Submit
              </button>
            </div>
          </div>
        </b-step-item>
      </template>
    </b-steps>
  </section>
</template>

<script>
import axios from "axios";
import firebase from "firebase";
import { GET, POST, tokenExpires } from "../../api";
import Loading from "../../components/Loading";
import Inspection from "../../components/Inspection";
import AuctionDetails from "../AuctionDetails.vue";

export default {
  name: "InspectorSteps",
  components: { Loading, Inspection, AuctionDetails },
  data() {
    return {
      activeStep: 0,
      vehicle: {},
      auctionExpectation: -1,
      loading: false,
      inspector: this.$store.state.user,
      originalOwner: null,
      imageLoading: false,
      vehiclePhotos: {
        exterior: {
          images: [],
        },
        damage: {
          images: [],
        },
        interior: {
          images: [],
        },
      },
      questionnaireData: [
        {
          title: "Does your vehicle have any aftermarket equipment?",
          response: "",
          commentary: "",
          placeholder: "If yes, what?",
        },
        {
          title: "Are you the original owner?",
          response: "",
          commentary: "",
        },
        {
          title: "Are you still making payments?",
          response: "",
          commentary: "",
          placeholder: "If yes, Who is your lienholder?",
        },
        {
          title:
            "Has it had any accidents or issues that may appear on a vehicle history report?",
          response: "",
          commentary: "",
        },
        {
          title:
            "Has an insurance company paid for any repairs on your vehicle?",
          response: "",
          commentary: "",
        },
        {
          title: "Do you have complete maintenance records?",
          response: "",
          commentary: "",
        },
        {
          title: "Do you have 2 or more keys?",
          response: "",
          commentary: "",
        },
        {
          title: "Has this vehicle been to an auction in the last 45 days?",
          response: "",
          commentary: "",
        },
        {
          title: "Has this car been used as a rental?",
          response: "",
          commentary: "",
        },
      ],
      expectationPriceData: [
        {
          id: "buyItNow",
          title: "Buy-it-now",
          commentary: "",
          response: "",
        },
        {
          id: "reserve",
          title: "Reserve",
          commentary: "",
          response: "",
        },
        {
          id: "askingPrice",
          title: "Starting-Price",
          commentary: "",
          response: "",
        },
      ],
      inspectionData: [],
    };
  },

  methods: {
    submitInspection() {
      return this.$buefy.dialog.confirm({
        title: `Finish Inspection`,
        message: "Confirm your inspection",
        onConfirm: () => {
          POST(`vehicles/inspect/submit/${this.$route.params.id}`, {
            inspectionData: this.inspectionData,
            questionnaireData: this.questionnaireData,
            vehiclePhotos: this.vehiclePhotos,
            auction: {
              buyItNowPrice: Number.parseInt(
                this.expectationPriceData[0].commentary
              ),
              reservePrice: Number.parseInt(
                this.expectationPriceData[1].commentary
              ),
              askingPrice: Number.parseInt(
                this.expectationPriceData[2].commentary
              ),
            },
          })
            .then(() => {
              this.$buefy.toast.open({
                message: "Inspection done successfully!",
                duration: 5000,
                type: "is-success",
              });
              this.$router.push("/inspector");
            })
            .catch((error) => {
              this.$buefy.toast
                .open({
                  message: error.response.data.message,
                  duration: 5000,
                  type: "is-danger",
                })
                .catch((error) => {
                  this.$buefy.toast.open({
                    message: error.response.data.message,
                    duration: 5000,
                    type: "is-danger",
                  });
                });
            });
        },
      });
    },
    remove(gallery, index) {
      const imageUrl = this.vehiclePhotos[gallery].images[index].url;

      const imgRefURL = firebase.storage().refFromURL(imageUrl);

      const imgRef = firebase
        .storage()
        .ref()
        .child(`${this.vehicle.id}/inspection/${gallery}/${imgRefURL.name}`);

      imgRef
        .delete()
        .then((response) => {
          this.$buefy.toast.open({
            message: "Picture deleted",
            type: "is-success",
          });
          this.vehiclePhotos[gallery].images.splice(index, 1);
        })
        .catch((error) => {
          this.$buefy.toast.open({
            message:
              "There was a problem removing your picture, please try again later.",
            type: "is-danger",
          });
        });
    },
    addImage(gallery) {
      this.vehiclePhotos[gallery].images.push({
        url:
          "https://user-images.githubusercontent.com/194400/49531010-48dad180-f8b1-11e8-8d89-1e61320e1d82.png",
        progress: 0,
      });
    },
    dataChanged(data) {
      this.inspectionData = data;
    },
    uploadImage(event, galleryIndex, imageIndex) {
      const files = [...event.target.files];
      this.imageLoading = true;

      files.forEach((file, index) => {
        const [name, extention] = file.name.split(".");

        const uniqueName = name + Date.now().toString() + "." + extention;

        const storageRef = firebase
          .storage()
          .ref(`${this.vehicle.id}/inspection/${galleryIndex}/${uniqueName}`);

        const task = storageRef.put(file);
        const images = this.vehiclePhotos[galleryIndex].images;
        let percentage;
        task.on(
          "state_changed",
          (snapshot) => {
            percentage =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          },
          (error) => console.log({ error }),
          () => {
            storageRef.getDownloadURL().then((url) => {
              this.vehiclePhotos[galleryIndex].images.push({
                progress: percentage,
                url,
              });

              this.imageLoading = false;
            });
          }
        );
      });
    },
  },
  async beforeCreate() {
    try {
      this.loading = true;

      const vehicleRes = await GET(`vehicles/${this.$route.params.id}`);
      this.vehicle = vehicleRes.data;

      const expectedPriceRes = await POST(
        "vin/auction-expectation",
        this.vehicle
      );

      this.auctionExpectation = expectedPriceRes.data.value;
      if (vehicleRes.data.inspectionData) {
        this.inspectionData = vehicleRes.data.inspectionData;
      }
      if (vehicleRes.data.vehiclePhotos) {
        this.vehiclePhotos = vehicleRes.data.vehiclePhotos;
      }
      if (vehicleRes.data.questionnaireData) {
        this.questionnaireData = vehicleRes.data.questionnaireData;
      }

      this.loading = false;
    } catch (error) {
      if (error.response.status === 401) {
        return tokenExpires();
      }
      this.$buefy.toast.open({
        message: error.response.data.message,
        duration: 3000,
        type: "is-danger",
      });
    }
  },
  computed: {
    priceSugestions() {
      return {
        buyItNow: Math.round(this.auctionExpectation * 1.05),
        askingPrice: Math.round(this.auctionExpectation * 0.85),
      };
    },
    isFormFilled() {
      const inspectionFilled = this.inspectionData.filter((inspection) =>
        inspection.conditions.filter((condition) => condition.inspectorResponse)
      );
      const questionnaireFilled = this.questionnaireData.filter(
        (question) => question.response
      );
      const photosFilled = Object.values(this.vehiclePhotos).map((gallery) => {
        if (gallery.images.length === 0) return false;
        return gallery.images.every((image) => image.progress === 100);
      });

      return (
        photosFilled.every((element) => element === true) &&
        questionnaireFilled &&
        inspectionFilled
      );
    },
    baseSteps() {
      return [
        {
          title: "Review Details",
          displayed: true,
        },
        {
          title: "Inspection",
          displayed: true,
        },
        {
          title: "Photos",
          displayed: true,
        },
        {
          title: "Questionnaire",
          displayed: true,
        },
        {
          title: "Preview",
          displayed: true,
        },
      ];
    },
    steps() {
      const steps = [...this.baseSteps];

      return steps;
    },
  },
  watch: {
    expectationPriceData: {
      deep: true,
      handler(expectationPriceData) {
        expectationPriceData.map((exp) => {
          if (exp.response === "Yes") {
            exp.commentary = this.priceSugestions[exp.id];
          }
        });
      },
    },
  },
};
</script>

<style lang="postcss">
.inspector-steps {
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  margin-top: 30px;

  & .submit-container {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  & .b-steps .steps + .step-content {
    height: auto;
  }

  & .b-steps {
    @media screen and (max-width: 600px) {
      margin-top: 30px;
    }
  }

  & .b-radio.radio.button {
    border-radius: 4px !important;
  }

  & .card {
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16078);
    border-radius: 4px;
    margin-bottom: 20px;

    & .card-title {
      display: flex;
      justify-content: space-between;
      background: var(--primary);
      padding: 22px 20px;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;

      & h3 {
        color: #fcfcfc;
        font-size: 20px;
      }
    }

    & .column {
      flex-direction: column;
    }

    & .details-container {
      box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16078);
      padding: 10px 15px;
      display: flex;

      & li {
        list-style: none;
        border-bottom: 1px solid #00000029;
        padding: 10px 15px;

        @media screen and (max-width: 600px) {
          font-size: 13px;
        }

        & .input-container {
          display: flex;
          padding: 10px 0 0;

          & .input-label {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;

            @media screen and (max-width: 600px) {
              font-size: 12px;
            }

            & input {
              margin-left: 5px;
              max-width: 60px;
              padding: 5px;
              border-radius: 4px;
              border: 1px solid rgba(0, 0, 0, 0.21961);
            }
          }
        }

        & .comment-section {
          display: flex;
          padding: 10px 0 0;

          & input {
            flex: 1;
            padding: 8px 10px;
            border-radius: 4px;
            border: 1px solid rgba(0, 0, 0, 0.21961);
          }
        }

        &:last-child {
          border-bottom: 0;
        }

        & .list-content {
          display: flex;
          justify-content: space-between;

          & .questionnaire-container {
            font-size: 14px;
            width: 100%;

            & h3 {
              margin-top: 20px;
              text-align: center;
              color: #52aa57;
              font-weight: bold;
            }

            & > span {
              color: #52aa57;
              font-weight: bold;
            }

            & em {
              color: #52aa57;
              font-weight: bold;
            }

            & .btn-selection {
              display: flex;
              justify-content: space-around;
              margin-top: 20px;
              flex: 1;

              & .field.has-addons {
                flex: 1;

                & .control {
                  flex: 1;
                }
              }
            }
          }

          & span {
            @media screen and (max-width: 600px) {
              max-width: 100px;
            }
          }

          & .checklist-container {
            display: flex;
            align-items: center;
            justify-content: space-around;

            & .field.has-addons {
              flex: 1;

              & .control {
                flex: 1;

                & .b-radio.radio.button {
                  flex: 1;
                }
              }
            }

            & input {
              margin-left: 10px;
              margin-right: 8px;
              border: 1px solid #b9b9b9;
              border-radius: 4px;

              @media screen and (max-width: 400px) {
                margin-left: 5px;
                margin-right: 5px;
              }
            }
          }

          & h4 {
            text-transform: capitalize;
          }

          & .inspector-details {
            display: flex;
            flex-direction: column;
          }

          & .report-flag {
            color: #d14848;
          }
        }
      }

      & .questionnaire-details {
        margin: 15px 5px;
        flex: 1;
        width: 100%;

        @media screen and (max-width: 400px) {
          min-width: 250px;
        }
      }

      & .details {
        margin: 15px 5px;
        border: 1px solid #00000038;
        border-radius: 4px;
        flex: 1;

        @media screen and (max-width: 400px) {
          min-width: 250px;
        }

        & .details-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid #00000029;
          padding: 10px 15px;

          & h2 img {
            margin-left: 10px;
          }
        }
      }

      @media screen and (max-width: 600px) {
        flex-direction: column;
      }
    }
  }

  & .photos-container {
    display: flex;
    max-width: 900px;
    align-self: center;
    margin: 0 auto;
    flex-direction: column;

    @media screen and (max-width: 800px) {
      justify-content: center;
    }

    & h4 {
      color: #52aa57;
      font-size: 20px;
      margin-top: 40px;
      text-transform: capitalize;
    }

    & .btn-container {
      display: flex;
      margin: 20px 0;
      box-shadow: 0px 3px 6px #00000029;
      border-radius: 4px;
      background: #fcfcfc;

      & button {
        flex: 1;
        background: 0;
        border: 0;
        padding: 20px 10px;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
    & .photo-added {
      display: flex;
      flex-direction: column-reverse;
    }
    & .photo-card {
      box-shadow: 0px 3px 6px #00000029;
      border-radius: 4px;
      background: #fcfcfc;
      margin-top: 20px;
      display: flex;
      flex-direction: column;
      & .car-photo {
        object-fit: cover;
      }
      & .progress {
        height: 5px;
      }
    }
    & .image-actions {
      display: flex;

      & > button {
        flex: 1;
        background-color: #f5f5f5;

        border: 1px solid #dbdbdb;
        border-radius: 4px;

        display: flex;
        justify-content: center;
        align-items: center;
      }
      & button:hover {
        background: #eeeeee;
      }
      & button:disabled:hover {
        background-color: #f5f5f5;
        cursor: not-allowed;
      }
      & div {
        flex: 1;

        & label {
          width: 100%;
          & span {
            width: 100%;
          }
        }
      }
    }
  }
}
</style>
